import { Component, Signal, ChangeDetectionStrategy, ElementRef, input, viewChild, computed, inject, Injector, InjectionToken } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { Result, WebElement } from '../../../../state-management/model/interfaces/state.model';
import { ElementsType, StyleType } from '../../../../state-management/model/enums/state.enums';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EditableTextWarningDialogComponent } from './editable-text-warning-dialog/editable-text-warning-dialog.component';
import { MetaConfigDialogComponent } from '../meta-config-dialog/meta-config-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { ClickOutsideDirective } from '../../../../directives/click-outside.directive';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

export const META_CONFIG_DATA = new InjectionToken<any>('META_CONFIG_DATA');

@Component({
  selector: 'app-editable-text',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    ClickOutsideDirective,
  ],
  templateUrl: './editable-text.component.html',
  styleUrl: './editable-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditableTextComponent {
  readonly textElementRef = viewChild.required<ElementRef>('textElement');

  readonly elemId = input.required<string>();
  readonly customClass = input<string | null>(null);

  public eType = ElementsType.Text;
  private sanitizer = inject(DomSanitizer);

  public isEditMode$: Signal<boolean> = this.stateService.isEditMode;
  public currentElement$!: Signal<Result<WebElement, string>>;
  public styles$!: Signal<Partial<Record<StyleType, string>>>;
  public hasElementHtmlValue$!: Signal<boolean>;
  public sanitizedHtmlValue$!: Signal<SafeHtml | null>;

  public showEditButton: boolean = false;
  public warningMessage: string = ''; // Nachricht für Warnung

  private lastValidValue: string = ''; // Speichert den letzten gültigen Wert
  private currentElementValue = ''; // Aktueller Wert des Texts (gebunden an die Komponente)

  ngOnInit() {
    this.currentElement$ = this.stateService.getCurrentElement(this.elemId());
    this.styles$ = this.stateService.getStylesForElement(this.elemId());
    this.hasElementHtmlValue$ = this.stateService.hasElementHtmlValue(this.elemId());
    
    this.sanitizedHtmlValue$ = computed(() => {
      const currentElement = this.currentElement$();
      if (currentElement.success && currentElement.payload.htmlValue) {
        return this.sanitizer.bypassSecurityTrustHtml(currentElement.payload.htmlValue);
      }
      return null;
    });

    const currentElement = this.currentElement$();
    this.currentElementValue = currentElement.success && currentElement.payload.value !== null ? currentElement.payload.value : '';
    this.lastValidValue = this.currentElementValue;
  }

  public onClickOutside(): void {
    console.log('Clicked outside editable text component');
    // Close edit button on click outside
    this.showEditButton = false;
  }

  public onEdit(): void {
    if (this.isEditMode$()) {
      this.showEditButton = true;
    }
  }

  public openConfig(event: Event) {
    event.stopPropagation();
    
    const currentElementResponse = this.currentElement$();
    if (!currentElementResponse.success) return;

    // Positionierungs-Strategie: "Klebe an der Unterkante des Elements"
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.textElementRef().nativeElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 10
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -10
        }
      ]);

    // Scroll-Strategie: Das Panel wandert beim Scrollen mit
    const scrollStrategy = this.overlay.scrollStrategies.reposition();

    // Overlay erstellen
    const overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'transparent-backdrop',
      panelClass: 'meta-config-overlay'
    });

    // Daten für den Injector vorbereiten
    const dataToPass = {
      elemId: this.elemId(),
      styleId: currentElementResponse.payload.styles,
      CSDElementRef: this.textElementRef(),
      eType: this.eType
    };

    // Injector erstellen
    const customInjector = Injector.create({
      providers: [{ provide: META_CONFIG_DATA, useValue: dataToPass }],
      parent: this.injector
    });

    // Komponente im Overlay instanziieren
    const portal = new ComponentPortal(MetaConfigDialogComponent, null, customInjector);
    const componentRef = overlayRef.attach(portal);

    // Visibility detection für Element
    const checkElementVisibility = () => {
      const element = this.textElementRef().nativeElement;
      const rect = element.getBoundingClientRect();
      const isVisible = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
      
      const overlayElement = overlayRef.overlayElement;
      if (overlayElement) {
        overlayElement.style.display = isVisible ? 'block' : 'none';
      }
    };

    // Scroll listener für Visibility-Check
    const scrollListener = () => {
      checkElementVisibility();
    };

    // Event listeners hinzufügen
    window.addEventListener('scroll', scrollListener, true);
    window.addEventListener('resize', scrollListener);

    // Schließen-Logik
    const cleanup = () => {
      window.removeEventListener('scroll', scrollListener, true);
      window.removeEventListener('resize', scrollListener);
      overlayRef.dispose();
      this.showEditButton = false;
    };

    overlayRef.backdropClick().subscribe(cleanup);
    
    // Close event von der Komponente
    componentRef.instance.closePanel.subscribe(cleanup);
  }

  public closeMetaConfig(): void {
    // This method is kept for compatibility but not used anymore
    console.log('closeMetaConfig called (deprecated)');
    this.showEditButton = false;
  }

  public onInput(event: Event) {
    const input = (event.target as HTMLElement).innerText.trim();

    const currentElement = this.currentElement$();
    const tag = currentElement.success ? currentElement.payload.tag as keyof typeof tagLimits : 'p';

    const tagLimits = {
      "h1": { maxLength: 50, maxWords: 10 },
      "h2": { maxLength: 150, maxWords: 30 },
      "h3": { maxLength: 250, maxWords: 50 },
      "p": { maxLength: 2500, maxWords: 500 },
      "span": { maxLength: 2500, maxWords: 500 },
    };

    const maxLength = tagLimits[tag]?.maxLength || 300;
    const maxWords = tagLimits[tag]?.maxWords || 60;

    const wordCount = input.split(/\s+/).length; // Zähle die Wörter im Input

    if (input.length === 0) {
      this.warningMessage = 'Der Text darf nicht vollständig gelöscht werden!';
      this.openWarningDialog();
      // setTimeout(() => this.clearWarning(), 3000); // Entfernt die Warnung nach 3 Sekunden
      (event.target as HTMLElement).innerText = this.lastValidValue; // Setzt den Text zurück
    } else if (input.length > maxLength) {
      this.warningMessage = `Der Text darf nicht länger als ${maxLength} Zeichen sein.`;
      this.openWarningDialog();
      (event.target as HTMLElement).innerText = this.lastValidValue;
    } else if (wordCount > maxWords) {
      this.warningMessage = `Der Text darf nicht mehr als ${maxWords} Wörter enthalten.`;
      this.openWarningDialog();
      (event.target as HTMLElement).innerText = this.lastValidValue;
    } else {
      this.lastValidValue = input; // Speichere den gültigen Wert
    }
  }

  public onBlur(_event: Event) {
    if (this.lastValidValue !== this.currentElementValue) {
      this.currentElementValue = this.lastValidValue;
      this.stateService.updateElement(this.elemId(), { value: this.lastValidValue });
    }
  }


  private openWarningDialog() {
    this.dialog.open(EditableTextWarningDialogComponent, {
      data: { message: this.warningMessage },
      panelClass: 'admin-theme'
    });
  }

  constructor(
    private dialog: MatDialog,
    private stateService: WebsiteStateService,
    private overlay: Overlay,
    private injector: Injector
  ) { }
}